<script lang="ts">
  import Icon from "./Icon.svelte";
  import type { Workflow } from "../lib/commands";

  interface Props {
    workflows: Workflow[];
    onSelect: (workflow: Workflow) => void;
  }

  let { workflows, onSelect }: Props = $props();

  // Get top 4 workflows
  let topWorkflows = $derived(workflows.slice(0, 4));
</script>

<section class="trending">
  <header class="trending-header">
    <div class="trending-title">
      <Icon name="trending-up" size={18} />
      <h2>Trending Workflows</h2>
    </div>
    <a href="/workflows" class="view-all">
      View all
      <Icon name="arrow-right" size={14} />
    </a>
  </header>

  <div class="workflows-grid">
    {#each topWorkflows as workflow, i}
      <button
        class="workflow-card"
        style="animation-delay: {i * 50}ms"
        onclick={() => onSelect(workflow)}
      >
        <div class="workflow-icon" class:official={workflow.source === "official"}>
          <Icon name={workflow.icon} size={20} />
        </div>

        <div class="workflow-content">
          <h3 class="workflow-name">{workflow.name}</h3>
          <p class="workflow-desc">{workflow.description}</p>
        </div>

        <div class="workflow-meta">
          <code class="workflow-command">{workflow.command}</code>
          {#if workflow.trending}
            <span class="trending-badge">
              <Icon name="trending-up" size={10} />
              Trending
            </span>
          {/if}
        </div>

        <div class="workflow-arrow">
          <Icon name="arrow-right" size={16} />
        </div>
      </button>
    {/each}
  </div>

  <div class="create-workflow">
    <div class="create-content">
      <h3>Create Custom Workflows</h3>
      <p>
        Build your own workflows by chaining commands together.
        Save time on repetitive DeFi tasks.
      </p>
    </div>
    <a href="/workflows/create" class="create-link">
      Learn how to create workflows
      <Icon name="external-link" size={14} />
    </a>
  </div>
</section>

<style>
  .trending {
    margin-top: var(--space-8);
  }

  .trending-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-6);
  }

  .trending-title {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--text-primary);
  }

  .trending-title h2 {
    font-size: 1.125rem;
    font-weight: 600;
  }

  .view-all {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    font-size: 0.875rem;
    color: var(--text-secondary);
    transition: color var(--transition-fast);
  }

  .view-all:hover {
    color: var(--text-primary);
  }

  .workflows-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-4);
  }

  @media (max-width: 640px) {
    .workflows-grid {
      grid-template-columns: 1fr;
    }
  }

  .workflow-card {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-5);
    background: var(--bg-secondary);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-lg);
    text-align: left;
    transition: all var(--transition-base);
    animation: fadeInUp var(--transition-slow) ease backwards;
    position: relative;
    overflow: hidden;
  }

  .workflow-card::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      transparent 0%,
      rgba(59, 130, 246, 0.03) 100%
    );
    opacity: 0;
    transition: opacity var(--transition-base);
  }

  .workflow-card:hover {
    border-color: var(--border-default);
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  .workflow-card:hover::before {
    opacity: 1;
  }

  .workflow-card:hover .workflow-arrow {
    opacity: 1;
    transform: translateX(0);
  }

  .workflow-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-tertiary);
    border-radius: var(--radius-md);
    color: var(--text-secondary);
  }

  .workflow-icon.official {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
    color: var(--accent-primary);
  }

  .workflow-content {
    flex: 1;
  }

  .workflow-name {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: var(--space-1);
  }

  .workflow-desc {
    font-size: 0.8125rem;
    color: var(--text-secondary);
    line-height: 1.4;
  }

  .workflow-meta {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .workflow-command {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    padding: var(--space-1) var(--space-2);
    background: var(--bg-tertiary);
    border-radius: var(--radius-sm);
    color: var(--text-tertiary);
  }

  .trending-badge {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.6875rem;
    font-weight: 500;
    padding: 2px 8px;
    background: rgba(59, 130, 246, 0.1);
    border-radius: var(--radius-full);
    color: var(--accent-primary);
  }

  .workflow-arrow {
    position: absolute;
    top: var(--space-4);
    right: var(--space-4);
    color: var(--text-tertiary);
    opacity: 0;
    transform: translateX(-4px);
    transition: all var(--transition-base);
  }

  .create-workflow {
    margin-top: var(--space-8);
    padding: var(--space-6);
    background: var(--bg-secondary);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-lg);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    flex-wrap: wrap;
  }

  .create-content h3 {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: var(--space-1);
  }

  .create-content p {
    font-size: 0.8125rem;
    color: var(--text-secondary);
    max-width: 400px;
  }

  .create-link {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--accent-primary);
    white-space: nowrap;
  }

  .create-link:hover {
    text-decoration: underline;
  }
</style>
